import { Grid, Popover, styled, Typography } from '@quillbot/design-system';
import { ArrowDropDown } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { getCgBreakpointDown } from 'src/citation-generator/utils';
import { useFieldConfig, useFieldValidator } from '../config';
import { useEditFormDispatch, useEditFormState } from '../context';
import { FieldOption, FormValueKeys } from '../types';

const StyledGrid = styled(Grid)`
  ${({ theme }) => getCgBreakpointDown(theme)} {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LabelContainer = styled(Grid)`
  width: 24%;
  padding-right: 10px;
  ${({ theme }) => getCgBreakpointDown(theme)} {
    width: 100%;
  }
`;

const Label = styled(Typography)`
  float: right;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.primary};
  ${({ theme }) => getCgBreakpointDown(theme)} {
    float: unset;
  }
`;

interface InputContainerProps {
  error: boolean;
  isHighlighted: boolean;
}
const InputContainer = styled(Grid)<InputContainerProps>`
  border: 1px solid
    ${({ theme, isHighlighted, error }) => {
      if (error) return theme.palette.error.main;

      if (isHighlighted) return theme.palette.secondary.main;

      return theme.palette.divider;
    }};
  border-radius: 3px;
  width: calc(76% / 2);
  height: 30px;

  ${({ theme }) => getCgBreakpointDown(theme)} {
    width: 100%;
    margin-top: 4;
  }
`;

const Option = styled(Grid)<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  height: 25px;
  cursor: pointer;
  padding-left: 8px;
  color: ${({ theme }) => theme.palette.text.secondary};
  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.palette.background.default : ''};

  &:hover {
    background-color: ${({ theme }) => theme.palette.background.default};
  }
`;

const SelectedInput = styled('div')`
  padding: 0px 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  width: 100%;
  cursor: pointer;
  flex: 1;
`;

const getDefaultOption = (options?: FieldOption<any>[]) =>
  (options as FieldOption<any>[])?.filter((op) => op.default)[0] || options?.[0] || { label: '' };

export function Dropdown(props: {
  rootClass?: string;
  valueKey: FormValueKeys;
  setter: Function;
  clearable?: boolean;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selectedLabel, setSelectedLabel] = useState('');
  const { rootClass, valueKey, setter, clearable } = props;
  const { label, show, options } = useFieldConfig(valueKey);
  const { error, warning } = useFieldValidator(valueKey);
  const value = useEditFormState((state) => state[valueKey]);
  const dispatch = useEditFormDispatch();

  useEffect(() => {
    if (show) {
      if (value !== 0 && !value && clearable) {
        dispatch(setter(valueKey, undefined));
      } else if (value !== 0 && !value && !clearable && options?.length) {
        const defaultOption = getDefaultOption(options);
        dispatch(setter(valueKey, defaultOption.value));
      }
    }
  }, [show]);

  useEffect(() => {
    if (value || value === 0) {
      const currLabel = (options as FieldOption<any>[]).find((op) => op.value === value)?.label;
      setSelectedLabel(currLabel || '');
    } else {
      setSelectedLabel('');
    }
  }, [value]);

  if (!show || !options?.length) {
    return null;
  }

  return (
    <>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        elevation={3}
        onClose={() => setAnchorEl(null)}
        TransitionProps={{
          enter: false,
          exit: false,
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Grid style={{ overflowX: 'hidden', overflowY: 'auto', maxHeight: 140 }}>
          {props.clearable && (
            <Option
              item
              isSelected={!value}
              onClick={() => {
                dispatch(setter(valueKey, ''));
                setAnchorEl(null);
              }}
            />
          )}

          {options?.map((option) => (
            <Option
              key={option.label}
              item
              isSelected={option.value === value}
              onClick={() => {
                dispatch(setter(valueKey, option.value));
                setAnchorEl(null);
              }}
              style={{
                width: anchorEl ? `calc(${window.getComputedStyle(anchorEl)?.width})` : undefined,
              }}
            >
              {option.label}
            </Option>
          ))}
        </Grid>
      </Popover>

      <StyledGrid container alignItems="center" className={rootClass}>
        <LabelContainer>
          <Label>{label}</Label>
        </LabelContainer>

        <InputContainer
          error={Boolean(error)}
          isHighlighted={warning as boolean}
          alignItems="center"
          display="flex"
          onClick={(e) => {
            setAnchorEl(e.currentTarget as HTMLDivElement);
          }}
        >
          <SelectedInput>{selectedLabel}</SelectedInput>

          <ArrowDropDown
            fontSize="small"
            style={{
              transform: anchorEl ? 'rotate(180deg)' : '',
              transition: 'transform 0.2s',
            }}
          />
        </InputContainer>
      </StyledGrid>
    </>
  );
}

Dropdown.defaultProps = {
  clearable: true,
};
